import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import * as actions from "../../../Redux/Actions";
import {
  showErrorToastMessage,
  showSuccessToastMessage,
} from "../../../component/Toasts/Toasts";
import "../readable-form.scss";

const empName = (e: any) =>
  [e?.candidateName, e?.middleName, e?.lastname].filter(Boolean).join(" ").trim() || "-";

const errText = (res: any, fallback: string) =>
  res?.error?.response?.data?.error?.error_description || fallback;

/**
 * Map a successor onto a critical role. Two ways in:
 *  1. the suggested pool — everyone holding the seat's designation, this client first;
 *  2. the org-wide search — anyone at all, for someone ready to step up a grade.
 */
const MapSuccessorModal = (props: any) => {
  const { role, setShowModal, handleApiCall } = props;
  const dispatch = useDispatch();

  const [pool, setPool] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const [term, setTerm] = useState("");
  const [results, setResults] = useState<any[] | null>(null);
  const [searching, setSearching] = useState(false);
  // the employee currently being mapped, so its Map button can't be double-clicked
  const [mappingId, setMappingId] = useState<string | null>(null);

  const roleId = role?._id;
  const seatTitle = role?.designation?.title || "";
  const seatClient = role?.client?.clientName || "";

  const loadPool = () => {
    if (!roleId) return;
    setLoading(true);
    dispatch(
      actions.getTalentPoolAction(roleId, (res: any) => {
        if (res?.error) showErrorToastMessage(errText(res, "Couldn't load the suggested pool"));
        else if (res?.data?.pool) setPool(res.data.pool);
        setLoading(false);
      })
    );
  };

  useEffect(() => {
    loadPool();
  }, [roleId]);

  const handleSearch = () => {
    const q = term.trim();
    if (q.length < 2) {
      showErrorToastMessage("Enter at least 2 characters to search.");
      return;
    }
    setSearching(true);
    dispatch(
      actions.searchEmployeesAction(q, roleId, (res: any) => {
        setSearching(false);
        if (res?.error) showErrorToastMessage(errText(res, "Search failed"));
        else setResults(res?.data || []);
      })
    );
  };

  const handleMap = (emp: any) => {
    if (mappingId) return;
    setMappingId(emp._id);
    dispatch(
      actions.addCandidateAction({ criticalRoleRef: roleId, employeeRef: emp._id }, (res: any) => {
        setMappingId(null);
        if (res?.error) {
          showErrorToastMessage(errText(res, "Failed to map candidate"));
        } else {
          showSuccessToastMessage(`${empName(emp)} mapped`);
          // refresh both lists so a mapped person can't be mapped twice
          loadPool();
          if (results) setResults(results.filter((r) => r._id !== emp._id));
          if (handleApiCall) handleApiCall();
        }
      })
    );
  };

  // Scrollable so the list can't push the Done button off the bottom of the modal.
  const table = (list: any[]) => (
    <div className="ms-table-scroll">
      <table className="table-main table table-sm">
        <thead className="table-head">
          <tr className="table-head-row">
            <th className="table-head-row">Employee</th>
            <th className="table-head-row">Employee ID</th>
            <th className="table-head-row">Designation</th>
            <th className="table-head-row">Client</th>
            <th className="table-head-row">KRA Score</th>
            <th className="table-head-row">Action</th>
          </tr>
        </thead>
        <tbody className="table-body">
          {list.map((e) => (
            <tr className="table-body-row" key={e._id}>
              <td className="ms-name">{empName(e)}</td>
              <td>{e.employeeId || "-"}</td>
              <td>{e.designation || "-"}</td>
              <td>{e.clientName || "-"}</td>
              <td>{e.latestScore ?? <span className="muted">Not scored</span>}</td>
              <td>
                {e.isSuccessorEligible ? (
                  <button
                    className="btn add-submit-button buttonsize compact-btn"
                    disabled={!!mappingId}
                    onClick={() => handleMap(e)}
                  >
                    {mappingId === e._id ? "Mapping…" : "Map"}
                  </button>
                ) : (
                  <span className="muted" title="Mark this employee 'Succession Eligible' in the Employee DB before mapping">
                    Not eligible
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="map-successor readable-table">
      <p className="ms-context">
        Choosing successors for <b>{seatTitle}</b>
        {seatClient && (
          <>
            {" "}
            at <b>{seatClient}</b>
          </>
        )}
        . Mapped people are assessed in <b>Development Plan</b>.
      </p>

      {/* Search first — HR usually arrives already knowing who they want. */}
      <section className="ms-section">
        <div className="ms-section-head">
          <h5>Search the organisation</h5>
        </div>
        <p className="muted ms-hint">
          For someone ready to step into this role from a different designation.
        </p>

        <div className="pool-search-row">
          <input
            className="form-control"
            placeholder="e.g. name, employee ID, designation, client"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
          <button
            className="btn add-submit-button buttonsize compact-btn"
            disabled={searching}
            onClick={handleSearch}
          >
            {searching ? "Searching…" : "Search"}
          </button>
          {results !== null && (
            <button
              className="btn cancel-btn buttonsize compact-btn"
              onClick={() => {
                setResults(null);
                setTerm("");
              }}
            >
              Clear
            </button>
          )}
        </div>

        {results !== null &&
          (results.length > 0 ? (
            <>
              <p className="muted ms-result-count">
                {results.length} match{results.length === 1 ? "" : "es"} for “{term}”
              </p>
              {table(results)}
            </>
          ) : (
            <p className="empty-note">
              No one matches “{term}”. Try a shorter term, or part of an employee ID.
            </p>
          ))}
      </section>

      {/* Suggested pool */}
      <section className="ms-section">
        <div className="ms-section-head">
          <h5>
            Already {seatTitle || "this designation"}
            <span className="count-chip">{pool.length}</span>
          </h5>
          {seatClient && <span className="muted">{seatClient} first, then other clients</span>}
        </div>

        {loading ? (
          <p className="ms-hint">Loading…</p>
        ) : pool.length > 0 ? (
          table(pool)
        ) : (
          <p className="empty-note">
            No one else holds this designation — use the search above.
          </p>
        )}
      </section>

      <div className="ms-footer">
        <button className="btn cancel-btn buttonsize compact-btn" onClick={() => setShowModal(false)}>
          Done
        </button>
      </div>
    </div>
  );
};

export default MapSuccessorModal;
